import React, { useMemo }  from 'react';
import style from './textarea.module.scss';
import useApp from 'hooks/useApp';
import useIsSendInputDisabled from 'hooks/useIsDisabled';
import { validateAttachment } from 'views/SendMessage';
import { getBrowser, postMessageToParent } from 'utils';
import Browser from '../../types';

interface Props {
    value: string;
    setText(t: string): void;
    sendMessage(): void;
    setAttachments: (value: React.SetStateAction<File[]>) => void;
    setError: (value: React.SetStateAction<string>) => void;
    title?: string;
    attachments: File[];
}

export const mapTypeName = (type: string): string => {
    switch (type.toLowerCase().split('/')[1]) {
        case 'pdf':
            return 'PDF Document';
        case 'png':
            return 'PNG Image';
        case 'jpg':
        case 'jpeg':
            return 'JPEG Image';
        case 'csv':
            return 'CSV File';
        case 'txt':
            return 'Text Document';
        case 'doc':
        case 'docx':
            return 'Word Document';
        default:
            return type;
    }
};

const TextArea: React.FC<Props> = ({ value, setText, setAttachments, setError, attachments }) => {
    let { sendMessagePlaceholder, attachmentsConfig } = useApp();
    const disabled = useIsSendInputDisabled();

    const onDrop = (evt: React.DragEvent<HTMLDivElement> | React.DragEvent<HTMLLabelElement>) => {
        const el = document.getElementById('send-message');
        el!.style.backgroundColor = '';
        if (!attachmentsConfig?.enabled) {
            return;
        }
        for (const file of evt.dataTransfer?.files || []) {
            if (!validateAttachment(file, setError, attachmentsConfig).valid) {
                continue;
            }
            setAttachments(oldArray => [...oldArray, file]);
        }
        evt.preventDefault();

        setTimeout(() => {
            document.getElementById(`sa_att_${attachments.length}`)?.scrollIntoView();
        }, 200);
        postMessageToParent('user-chat-input-focus', {});
    };

    const onDragEnter = () => {
        const el = document.getElementById('send-message');
        el!.style.backgroundColor = '#C9C9C9';
        sendMessagePlaceholder = 'Upload...';
    };

    const onDragLeave = () => {
        const el = document.getElementById('send-message');
        el!.style.backgroundColor = '';
    };

    const removeAttachment = (fileName: string) => {
        if (!attachmentsConfig?.enabled) {
            return;
        }
        setAttachments(oldArray => oldArray.filter(x => x.name !== fileName));
    };

    const autoCompleteAttribute = useMemo(
        () => getBrowser() === Browser.SAFARI ? 'off' : 'cc-csc',
        [],
    );

    return (
        <div
            className={style.textArea}
            onDrop={onDrop}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
        >
            <label
                aria-hidden={true}
                className={`${disabled ? 'accessibility-hidden' : ''}`}
                htmlFor="send-message"
            >
                {value.length > 0 ? '' : sendMessagePlaceholder}
            </label>
            <input
                aria-label={sendMessagePlaceholder}
                autoComplete={autoCompleteAttribute}
                id="send-message"
                disabled={disabled}
                onChange={e => setText(e.target.value)}
                value={value}
                onFocus={() => postMessageToParent('user-chat-input-focus', {})}
                onBlur={() => postMessageToParent('user-chat-input-blur', {})}
            />

            {attachmentsConfig?.enabled && attachments?.length > 0 && (
                <div className={style.attachmentsContainer}>
                    {attachments.length > 0 &&
                        attachments.map((file, id) => (
                            <>
                                <div
                                    className={style.attachments}
                                    key={file.name}
                                    id={`sa_att_${id.toString()}`}
                                    tabIndex={0}
                                >
                                    {file.type.split('/')[0].toLowerCase() !== 'image' ? (
                                        <svg
                                            width="28"
                                            height="36"
                                            viewBox="0 0 28 36"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M2.75 36C1.98333 36 1.33333 35.7333 0.8 35.2C0.266667 34.6667 0 34.0167 0 33.25V2.75C0 1.98333 0.266667 1.33333 0.8 0.8C1.33333 0.266667 1.98333 0 2.75 0H19.45L28 8.55V33.25C28 34.0167 27.7333 34.6667 27.2 35.2C26.6667 35.7333 26.0167 36 25.25 36H2.75ZM2.75 34.45H25.25C25.55 34.45 25.825 34.325 26.075 34.075C26.325 33.825 26.45 33.55 26.45 33.25V9.2H18.7V1.55H2.75C2.45 1.55 2.175 1.675 1.925 1.925C1.675 2.175 1.55 2.45 1.55 2.75V33.25C1.55 33.55 1.675 33.825 1.925 34.075C2.175 34.325 2.45 34.45 2.75 34.45ZM1.55 33.25V1.55C1.55 1.55 1.55 1.675 1.55 1.925C1.55 2.175 1.55 2.45 1.55 2.75V33.25Z"
                                                fill="#353535"
                                            />
                                        </svg>
                                    ) : (
                                        <img
                                            alt=""
                                            className={style.previewImage}
                                            src={getImageSrc(file)}
                                        />
                                    )}
                                    <div className={style.attachmentInformation}>
                                        <div className={style.attachmentName}>{file.name}</div>
                                        <div className={style.attachmentName}>
                                            {mapTypeName(file.type)}
                                        </div>
                                    </div>
                                    <div
                                        className={style.removeAttachmentButtonContainer}
                                        tabIndex={0}
                                    >
                                        <div
                                            className={style.removeAttachmentButton}
                                            onClick={() => removeAttachment(file.name)}
                                        >
                                            x
                                        </div>
                                    </div>
                                </div>
                            </>
                        ))}
                </div>
            )}
        </div>
    );
};

const getImageSrc = (file: File) => window.URL.createObjectURL(file);

export default TextArea;
